<template>
    <div>
        <!-- Loading skeleton -->
        <div v-if="loading" class="tw:flex tw:flex-col tw:gap-3">
            <Skeleton customClass="tw:mb-3" padding="tw:py-[10px] tw:px-4">
                <template #skeleton>
                    <SkeletonItem customClass="tw:w-28 tw:mt-2" height="tw:h-5" />
                    <div class="tw:flex tw:justify-between tw:items-center tw:mb-1">
                        <div class="tw:flex tw:flex-col tw:py-1 tw:gap-4">
                            <div class="tw:flex  tw:flex-col tw:gap-3">
                                <SkeletonItem customClass="tw:w-[78px] tw:rounded-full" height="tw:h-5" />
                                <SkeletonItem customClass="tw:w-44" height="tw:h-4" />
                            </div>
                        </div>
                        <div class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-4 tw:border tw:border-gray-100 tw:shadow tw:rounded-md tw:px-5 tw:h-[70px]">
                            <div class="tw:flex tw:gap-5 tw:items-center">
                                <SkeletonItem customClass="tw:w-7" height="tw:h-[18px]" />
                                <SkeletonItem customClass="tw:w-4" height="tw:h-4" />
                            </div>
                            <SkeletonItem customClass="tw:w-16" height="tw:h-3" />
                        </div>
                    </div>
                    
                    <Skeleton
                        v-for="n in 3"
                        :key="n"
                        customClass="tw:mb-1"
                        padding="tw:p-4"
                    >
                        <template #skeleton>
                            <div class="tw:flex tw:items-center tw:justify-between">
                                <div class="tw:flex tw:items-center tw:gap-3">
                                    <SkeletonItem customClass="tw:w-10 tw:h-10 tw:rounded-full tw:shrink-0" />
                                    <div class="tw:flex tw:flex-col tw:gap-1">
                                        <SkeletonItem customClass="tw:w-32" height="tw:h-4" />
                                        <SkeletonItem customClass="tw:w-24" height="tw:h-3" />
                                    </div>
                                </div>
                                <div class="tw:flex tw:items-center tw:gap-3">
                                    <SkeletonItem customClass="tw:w-14" height="tw:h-5" />
                                    <SkeletonItem customClass="tw:w-5 tw:h-5 tw:rounded-full" />
                                </div>
                            </div>
                        </template>
                    </Skeleton>
                </template>
            </Skeleton>

        </div>

        <!-- Error -->
        <div
            v-else-if="error"
            class="tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:p-6 tw:text-center"
        >
            <p class="tw:text-sm tw:text-red-700">{{ error }}</p>
            <Button
                variant="ghost"
                custom-class="tw:mt-3"
                @click="onRetry"
            >
                {{ $t('perfil.familia.reintentar') }}
            </Button>
        </div>

        <!-- Empty state -->
        <div
            v-else-if="isEmpty"
            class="tw:rounded-xl tw:bg-white tw:border tw:border-slate-200 tw:flex tw:flex-col tw:items-center tw:gap-4 tw:py-10 tw:px-8"
        >
            <i class="material-icons tw:text-5xl tw:text-slate-300">groups</i>
            <p class="tw:text-base tw:font-semibold tw:text-gray-700">
                {{ $t('perfil.familia.empty_titulo') }}
            </p>
            <p class="tw:text-sm tw:text-gray-500 tw:max-w-md tw:text-center">
                {{ $t('perfil.familia.empty_descripcion') }}
            </p>
        </div>

        <!-- Con familia -->
        <template v-else>
            <CardFront custom-classes="tw:px-4 tw:py-4 tw:mb-6">
                <div class="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:mb-2">
                    <div class="tw:flex tw:items-center tw:gap-1">
                        <h2 class="tw:text-lg tw:font-extrabold tw:text-slate-900 tw:m-0 tw:uppercase">
                            {{ familia.nombre ?? $t('perfil.familia.sin_nombre') }}
                        </h2>
                        <button
                            v-if="isTitularMember"
                            class="tw:inline-flex tw:items-center tw:justify-center tw:w-8 tw:h-8 tw:rounded-lg tw:text-blue-600 hover:tw:bg-blue-50 tw:transition-colors"
                            :title="$t('perfil.familia.editar_nombre_titulo')"
                            @click="showEditNombreModal = true"
                        >
                            <i class="material-icons tw:text-lg">edit</i>
                        </button>
                    </div>
                    <BadgeFront
                        v-if="estadoBadge"
                        :variant="estadoBadge.variant"
                        :label="estadoBadge.label"
                        :dot="false"
                    />
                </div>
                <div
                    v-if="estadoBadge?.info"
                    class="tw:rounded-md tw:border tw:p-3 tw:mb-4 tw:text-sm"
                    :class="{
                        'tw:border-green-200 tw:bg-green-50 tw:text-green-800': familia?.estado === 'aprobado',
                        'tw:border-amber-200 tw:bg-amber-50 tw:text-amber-800': familia?.estado === 'pendiente',
                        'tw:border-red-200 tw:bg-red-50 tw:text-red-800': familia?.estado === 'rechazado',
                    }"
                >
                    <i class="material-icons tw:text-base tw:align-middle tw:mr-1">info</i>
                    {{ estadoBadge.info }}
                    <template v-if="familia && (familia.estado === 'aprobado' || familia.estado === 'rechazado')">
                        <div
                            v-if="(familia.estado === 'rechazado' && familia.motivo_rechazo) || (familia.revisado_por && familia.revisado_en)"
                            class="tw:mt-2 tw:pt-2 tw:border-t tw:border-inherit"
                        >
                            <p v-if="familia.estado === 'rechazado' && familia.motivo_rechazo" class="tw:font-semibold tw:m-0">
                                {{ $t('perfil.familia.motivo_rechazo_label') }}: {{ familia.motivo_rechazo }}
                            </p>
                            <p v-if="familia.revisado_por" class="tw:text-xs tw:opacity-75 tw:m-0 tw:mt-1">
                                {{ $t('perfil.familia.revisado_por_label') }}: {{ familia.revisado_por.name }} &middot; {{ $t('perfil.familia.revisado_en_label') }}: {{ familia.revisado_en || '—' }}
                            </p>
                        </div>
                    </template>
                </div>
                <section class="tw:flex tw:items-center tw:justify-between tw:mb-4">
                    <div class="tw:flex tw:flex-col tw:gap-3">
                        <BadgeFront
                            variant="warning"
                            :label="$t('perfil.familia.titular')"
                            icon="star"
                            :dot="false"
                            class="tw:w-fit"
                        />
                        <span class="tw:text-slate-900 tw:font-bold">{{ titularNombre }}</span>
                    </div>
                    <CardFront custom-classes="tw:py-0 tw:px-5 tw:text-gray-500 tw:rounded-md">
                        <div class="tw:flex tw:items-center tw:justify-between">
                            <i class="material-icons tw:text-3xl">groups</i>
                            <span class="tw:text-lg tw:font-bold">{{ familia.miembros.length }}</span>
                        </div>
                        <p class="tw:m-0">{{ $t('perfil.familia.miembros') }}</p>
                    </CardFront>
                </section>

                <div class="tw:flex tw:flex-col tw:gap-3">
                    <FamiliaCard
                        v-for="miembro in miembros"
                        :key="miembro.id"
                        :miembro="miembro"
                        :is-titular="isTitular(miembro.id)"
                        :is-current-user="String(miembro.id) === String(props.abonoId)"
                        :can-remove="canRemoveMember(miembro.id)"
                        @remove="onRemoveMember"
                    />
                </div>

                <Button
                    v-if="isTitularMember"
                    variant="success-soft"
                    custom-class="tw:mt-4 tw:w-full"
                    icon-name="add"
                    @click="showAddModal = true"
                >
                    {{ $t('perfil.familia.aniadir') }}
                </Button>

                <!-- Pagination -->
                <CardFront
                    v-if="!loading && meta.last_page > 1"
                    custom-classes="tw:mt-3 tw:py-2 tw:px-4"
                >
                    <PaginationFront
                        :value="page"
                        :total="meta.total || 0"
                        :per-page="perPage"
                        @input="fetchMiembros(props.abonoId, $event, perPage)"
                    />
                </CardFront>
            </CardFront>

        </template>

        <!-- Add modal -->
        <FamiliaAddModal
            v-if="showAddModal"
            :validar-miembro="validarMiembro"
            :agregar-miembro="agregarMiembro"
            @close="showAddModal = false"
            @added="onMiembroAdded"
        />

        <!-- Remove confirmation modal -->
        <BaseModalFront
            v-if="showRemoveModal"
            :show="showRemoveModal"
            :title="$t('perfil.familia.remove_modal_titulo')"
            :confirm-text="pendingMemberName
                ? $t('perfil.familia.remove_modal_confirmar', { nombre: pendingMemberName })
                : $t('perfil.familia.remove_modal_confirmar_default')"
            :confirm-disabled="removeLoading"
            :loading="removeLoading"
            confirm-variant="danger"
            @confirm="onConfirmRemove"
            @close="onCancelRemove"
        >
            <p class="tw:text-sm tw:text-slate-600" v-html="$t('perfil.familia.remove_modal_confirmacion', { nombre: `<strong>${pendingMemberName}</strong>` })">
            </p>
            <p v-if="isTitularMember && String(pendingRemoveId) !== String(props.abonoId)" class="tw:text-xs tw:text-slate-500 tw:mt-2">
                {{ $t('perfil.familia.remove_modal_titular_info') }}
            </p>
            <p v-if="String(pendingRemoveId) === String(props.abonoId) && !isTitularMember" class="tw:text-xs tw:text-slate-500 tw:mt-2">
                {{ $t('perfil.familia.remove_modal_abandono_info') }}
            </p>
            <p v-if="removeError" class="tw:text-sm tw:text-red-600 tw:mt-3">
                {{ removeError }}
            </p>
        </BaseModalFront>

        <!-- Edit nombre modal -->
        <BaseModalFront
            v-if="showEditNombreModal"
            :show="showEditNombreModal"
            :title="$t('perfil.familia.editar_nombre_titulo')"
            :confirm-text="$t('perfil.familia.editar_nombre_guardar')"
            :confirm-disabled="editNombreLoading || !editNombreValue.trim() || editNombreValue.trim() === familia?.nombre"
            :loading="editNombreLoading"
            confirm-variant="primary"
            @confirm="onConfirmEditNombre"
            @close="onCancelEditNombre"
        >
            <div class="tw:flex tw:flex-col tw:gap-3">
                <label class="tw:text-sm tw:font-medium tw:text-slate-700">{{ $t('perfil.familia.editar_nombre_label') }}</label>
                <input
                    ref="editNombreInput"
                    v-model="editNombreValue"
                    type="text"
                    class="tw:border tw:border-slate-300 tw:rounded-md tw:px-3 tw:py-2 tw:text-sm tw:focus:tw:outline-none tw:focus:tw:ring-2 tw:focus:tw:ring-blue-500"
                    :placeholder="$t('perfil.familia.editar_nombre_placeholder')"
                />
            </div>
            <p v-if="editNombreError" class="tw:text-sm tw:text-red-600 tw:mt-3">{{ editNombreError }}</p>
        </BaseModalFront>
    </div>
</template>

<script setup>
    import { ref, computed, watch } from 'vue';
    import Button from '@/modules/front/ui/Button.vue';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import BaseModalFront from '@/modules/front/ui/BaseModalFront.vue';
    import PaginationFront from '@/modules/front/ui/pagination/PaginationFront.vue';
    import { Skeleton, SkeletonItem } from '@/modules/front/ui/skeleton';
    import FamiliaCard from '../components/familia/Card.vue';
    import FamiliaAddModal from '../components/familia/AddModal.vue';
    import { useFamilia } from '@/composables/front/perfil/useFamilia';
    import BadgeFront from '@/modules/front/ui/BadgeFront.vue';
    import { t } from '@/utils/i18n-helper';

    const props = defineProps({
        abonoId: {
            type: [Number, String],
            required: true,
        },
        nombreAbonado: {
            type: String,
            default: null,
        },
        nAbonado: {
            type: [Number, String],
            default: null,
        },
    });

    const emit = defineEmits(['close']);

    const {
        familia,
        miembros,
        isEmpty,
        miembroCount,
        loading,
        error,
        meta,
        page,
        perPage,
        isTitular,
        fetchFamilia,
        fetchMiembros,
        validarMiembro,
        agregarMiembro,
        removerMiembro,
        actualizarNombre,
    } = useFamilia();

    const showAddModal = ref(false);
    const showRemoveModal = ref(false);
    const pendingRemoveId = ref(null);
    const removeLoading = ref(false);
    const removeError = ref(null);

    // Edit nombre state
    const showEditNombreModal = ref(false);
    const editNombreValue = ref('');
    const editNombreLoading = ref(false);
    const editNombreError = ref(null);

    const titularNombre = computed(() => {
        const t = familia.value?.titular;
        if (!t) return '—';
        return `${t.name ?? ''} ${t.surnames ?? ''}`.trim() || '—';
    });

    const estadoBadge = computed(() => {
        const estado = familia.value?.estado;
        if (!estado) return null;
        const map = {
            aprobado: {
                variant: 'success',
                label: t('perfil.familia.estado_aprobada', {}, 'Verificada'),
                info: t('perfil.familia.estado_aprobada_info', {}, 'Tu grupo familiar está verificado. Disfrutarás de los precios especiales para familias si el club los tiene disponibles.'),
            },
            pendiente: {
                variant: 'warning',
                label: t('perfil.familia.estado_pendiente', {}, 'Pendiente de verificación'),
                info: t('perfil.familia.estado_pendiente_info', {}, 'Tu grupo está pendiente de verificación por parte del club. Puedes continuar con la renovación conjunta, pero sin los beneficios de precios para familias.'),
            },
            rechazado: {
                variant: 'danger',
                label: t('perfil.familia.estado_rechazada', {}, 'Rechazada'),
                info: t('perfil.familia.estado_rechazada_info', {}, 'Tu grupo familiar ha sido rechazado. Puedes continuar con la renovación conjunta a precios normales.'),
            },
        };
        return map[estado] ?? null;
    });

    const isTitularMember = computed(() => {
        const t = familia.value?.titular;
        return t && String(t.id) === String(props.abonoId);
    });

    const canRemoveMember = memberId => {
        // Titular puede quitar a cualquiera (incluido él mismo)
        if (isTitularMember.value) return true;
        // Miembro regular solo puede abandonar (quitarse a sí mismo)
        return String(memberId) === String(props.abonoId);
    };

    const onRemoveMember = memberId => {
        pendingRemoveId.value = memberId;
        removeError.value = null;
        showRemoveModal.value = true;
    };

    const onConfirmRemove = async() => {
        if (!pendingRemoveId.value) return;
        removeLoading.value = true;
        removeError.value = null;
        try {
            const result = await removerMiembro(pendingRemoveId.value);
            showRemoveModal.value = false;
            pendingRemoveId.value = null;
            if (result.deleted) {
                // La familia se eliminó, volver a la vista de abonos
                emit('close');
            }
            // Si sobrevive, los miembros ya se refrescaron en el composable
        } catch (err) {
            if (err.response?.status === 403) {
                removeError.value = t('perfil.familia.remove_modal_error_permiso', {}, 'No tienes permiso para realizar esta acción.');
            } else if (err.response?.status === 422) {
                removeError.value = err.response?.data?.message ?? t('perfil.familia.remove_modal_error_desvincular', {}, 'No se puede desvincular este miembro.');
            } else {
                removeError.value = t('perfil.familia.remove_modal_error_generico', {}, 'Ocurrió un error. Inténtalo de nuevo.');
            }
        } finally {
            removeLoading.value = false;
        }
    };

    const onCancelRemove = () => {
        showRemoveModal.value = false;
        pendingRemoveId.value = null;
        removeError.value = null;
    };

    const pendingMemberName = computed(() => {
        if (!pendingRemoveId.value) return '';
        const m = miembros.value.find(m => String(m.id) === String(pendingRemoveId.value));
        if (!m) return '';
        const isSelf = String(m.id) === String(props.abonoId);
        return isSelf ? t('perfil.familia.remove_modal_tu_abono', {}, 'tu abono del grupo') : `${m.name} ${m.surnames}`;
    });

    // Watch to pre-populate input when modal opens
    watch(showEditNombreModal, val => {
        if (val) {
            editNombreValue.value = familia.value?.nombre || '';
            editNombreError.value = null;
        }
    });

    const onConfirmEditNombre = async() => {
        const nombre = editNombreValue.value.trim();
        if (!nombre || nombre === familia.value?.nombre) return;
        editNombreLoading.value = true;
        editNombreError.value = null;
        try {
            await actualizarNombre(nombre);
            showEditNombreModal.value = false;
            // useFamilia ya refrescó fetchFamilia en actualizarNombre
        } catch (err) {
            if (err.response?.data?.error) {
                editNombreError.value = err.response.data.error;
            } else if (err.response?.data?.message) {
                editNombreError.value = err.response.data.message;
            } else {
                editNombreError.value = t('perfil.familia.editar_nombre_error', {}, 'Error al actualizar el nombre');
            }
        } finally {
            editNombreLoading.value = false;
        }
    };

    const onCancelEditNombre = () => {
        showEditNombreModal.value = false;
        editNombreValue.value = '';
        editNombreError.value = null;
    };

    const onRetry = () => {
        fetchFamilia(props.abonoId);
    };

    const onMiembroAdded = () => {
        showAddModal.value = false
        fetchFamilia(props.abonoId)
    };

    // Fetch when abonoId changes (navigating to this view)
    watch(
        () => props.abonoId,
        newId => {
            if (newId) {
                fetchFamilia(newId);
            }
        },
        { immediate: true }
    );
</script>
